import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
// TODO: update to actual icon from design system
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import type { CSSObject, DrawerProps } from '@mui/material';
import { Box, Drawer, IconButton, List, ListItem, Typography, useMediaQuery, useTheme } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { styled } from '@mui/material/styles';

import { getUser, getUserRoleIds, logout } from '../../data/service/authService';
import { useFeatureFlags } from '../../hooks';

import { ReactComponent as Logo } from '@/assets/images/Alternaleaf_Logo.svg';
import { COLLAPSIBLE_MENU_ICON, collapsibleMenu } from '@/tours/CollapsibleMenu';
import { useFeatureTourVisibility } from '@/tours/hooks/useFeatureTourVisibility';
import { FeatureName } from '@/tours/types';
import AccountCircle from '@mui/icons-material/AccountCircle';
import Logout from '@mui/icons-material/Logout';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { useTour } from '@reactour/tour';
import { UserPreferencesDrawer } from '../UserPreferencesDrawer';
import { DropDownMenu } from './DropDownMenu';
import { MenuButton } from './MenuButton';
import type { MenuItemWithChildren } from './MenuItems';
import { DEFAULT_MENUS } from './MenuItems';
import { MenuNavButton } from './MenuNavButton';

const PREFIX = 'SideBar';

const DRAWER_WIDTH_OPEN = '300px';

export const DRAWER_WIDTH_CLOSE = '72px';

export const classes = {
  root: `${PREFIX}-root`,
  menuToggle: `${PREFIX}-menuToggle`,
  menuLink: `${PREFIX}-menuLink`,
  activeMenuLink: `active`, // default class name set by react-router-dom when the link is active
  title: `${PREFIX}-title`,
  listItem: `${PREFIX}-listItem`,
  link: `${PREFIX}-link`,
  logoImg: `${PREFIX}-logoImg`,
  profile: `${PREFIX}-profile`,
};

const hideScrollBar: CSSObject = {
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  msOverflowStyle: 'none',
  scrollbarWidth: 'none',
};
/**
 * Shared transitions for the drawer animation
 */
const miniVariantDrawerTransitions = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

/**
 * Used to add the "mini variant" effect for the Drawer and the Paper component contained within
 */
const toggleDrawerMixin = (theme: Theme, open?: boolean): CSSObject => ({
  width: open ? DRAWER_WIDTH_OPEN : DRAWER_WIDTH_CLOSE,
  ...miniVariantDrawerTransitions(theme),
});

const StyledDrawer = styled(Drawer)<DrawerProps>(({ theme, open }) => [
  {
    flexShrink: 0,
    ...toggleDrawerMixin(theme, open),
    '& .MuiDrawer-paper': {
      border: 'none',
      backgroundColor: '#ceedff',
      boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.15)',
      ...toggleDrawerMixin(theme, open),
      ...hideScrollBar,
    },
    '& *.menuButtonText': {
      display: open ? {} : 'none',
    },
    [`& .${classes.menuToggle}`]: {
      padding: theme.spacing(6, 4),
      '& .MuiSvgIcon-root': {
        color: theme.palette.primary.main,
        transform: open ? {} : 'scaleX(-1)',
      },
    },
    [`& .${classes.listItem}`]: {
      padding: open ? theme.spacing(0, 1) : 0,
      ...(!open && {
        '& > .dropDropMenuDiv': {
          margin: '10px 8px',
          '& > span': {
            borderRadius: '4px',
            alignItems: 'center',
            justifyContent: 'center',
          },
        },
        '& > a': {
          margin: '10px 8px',
          '& > span': {
            borderRadius: '4px',
            alignItems: 'center',
            justifyContent: 'center',
          },
        },
        '& > span': {
          borderRadius: '4px',
          alignItems: 'center',
          justifyContent: 'center',
        },
      }),
    },
    [`& .${classes.logoImg}`]: {
      height: '40px',
      overflowX: 'hidden',
      padding: open ? theme.spacing(0, 7) : theme.spacing(0, 0, 0, 4),
      boxSizing: 'content-box',
      margin: theme.spacing(5, 0),
      ...(open ? {} : { width: '40px' }),
      ['& a']: {
        display: 'flex',
        overflowX: 'hidden',
      },
      ['& svg']: {
        fill: theme.palette.primary.main,
      },
    },
    [`& .${classes.profile}`]: {
      justifyContent: 'center',
      textWrap: 'nowrap',
      '& > svg': {
        width: '100%',
      },
      ...(!open && {
        '& > button': {
          overflowX: 'hidden',
          padding: theme.spacing('6px', 4),
          justifyContent: 'flex-start',
          '& > svg': {
            marginRight: theme.spacing(2),
            padding: theme.spacing(0, 4.5),
            boxSizing: 'content-box',
          },
        },
      }),
    },
  },
  theme.applyStyles('dark', {
    '& .MuiDrawer-paper': {
      backgroundColor: theme.palette.primary.dark,
    },
  }),
]);

const StyledListItem = styled(ListItem, {
  shouldForwardProp: (prop: string) => !['isDrawerOpen'].includes(prop as string),
})<{ isDrawerOpen: boolean }>(({ isDrawerOpen }) => ({
  padding: '0rem 0.25rem',
  ['.MuiButtonBase-root']: {
    ...(!isDrawerOpen && { justifyContent: 'center' }),
  },
}));

const GenerateRoleIdsFromChildItems = (menuItem: MenuItemWithChildren) => {
  let roleIds: number[] = menuItem.permittedRoles ?? [];

  // Not really worth it to de-dedupe role ids
  if (menuItem.children) {
    menuItem.children.forEach((child) => {
      roleIds = roleIds.concat(GenerateRoleIdsFromChildItems(child));
    });
  }

  return roleIds;
};

const MenuItemButton = (
  menuItem: MenuItemWithChildren,
  navLinks: React.RefObject<HTMLAnchorElement | null>[] = [],
  isChild: boolean,
  drawerOpen: boolean,
): React.JSX.Element | null => {
  const { flags } = useFeatureFlags();
  const user = getUser();

  const userRoleIds = getUserRoleIds();
  const roleIds = GenerateRoleIdsFromChildItems(menuItem);
  const hasPermission = roleIds && roleIds.length > 0 ? userRoleIds.some((id) => roleIds.includes(id)) : false;

  const featureFlagEnabled = menuItem.featureFlag !== undefined ? flags[menuItem.featureFlag] : true;
  const accessCheckResult = menuItem.accessCheckMethod !== undefined ? menuItem.accessCheckMethod(user, flags) : true;

  if (!hasPermission || !featureFlagEnabled || !accessCheckResult) {
    return null;
  }

  if (menuItem.children && menuItem.children.length === 0) {
    return null;
  }

  const navLinkRef = React.createRef<HTMLAnchorElement | null>();
  if (menuItem.children === undefined) {
    navLinks.push(navLinkRef);
  }

  const isInDropDown = navLinks.length > 0 && isChild;

  return (
    <ListItem key={menuItem.id} className={classes.listItem}>
      <>
        {menuItem.children === undefined && (
          <MenuNavButton
            key={menuItem.id}
            url={menuItem.link ?? '#'}
            end={!menuItem.activeLinkStateIncludesNested}
            icon={menuItem.icon}
            name={menuItem.name}
            isSideBarOpen={drawerOpen}
            enableToolTip={!isInDropDown}
            navRef={navLinkRef}
          />
        )}

        {menuItem.children !== undefined && (
          <DropDownMenu name={menuItem.name} icon={menuItem.icon} navLinks={navLinks} sideBarOpen={drawerOpen}>
            {menuItem.children.map((child) => {
              return MenuItemButton(child, navLinks, true, drawerOpen);
            })}
          </DropDownMenu>
        )}
      </>
    </ListItem>
  );
};

export const SideBar = () => {
  const { setSteps, setIsOpen } = useTour();
  const user = getUser();

  const theme = useTheme();
  const isNotDesktop = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(!isNotDesktop);

  const [userPreferences, showUserPreferences] = useState(false);

  useEffect(() => {
    if (isNotDesktop) {
      setDrawerOpen(false);
    }
  }, [isNotDesktop]);

  const menus = useMemo(() => {
    return DEFAULT_MENUS;
  }, []);

  const { openFeatureTour: openCollapsibleMenuFeatureTour, handleDismissTour: dismissCollapsibleMenuTour } =
    useFeatureTourVisibility(FeatureName.CollapsibleMenu);

  useEffect(() => {
    if (openCollapsibleMenuFeatureTour) {
      setSteps && setSteps(collapsibleMenu(dismissCollapsibleMenuTour));
      setIsOpen(true);
    }
  }, [openCollapsibleMenuFeatureTour, setIsOpen, setSteps, dismissCollapsibleMenuTour]);

  const handleUserPreferencesDrawer = () => {
    showUserPreferences(!userPreferences);
  };

  return (
    <StyledDrawer className={classes.root} variant="permanent" open={drawerOpen}>
      <Box justifyContent="space-between" display="flex" flexDirection="column" height="100vh" alignContent="center">
        <Box>
          <Box className={classes.menuToggle}>
            <IconButton
              data-tour-selector={COLLAPSIBLE_MENU_ICON}
              aria-label="menu toggle"
              disableRipple
              onClick={() => setDrawerOpen((prevState) => !prevState)}
            >
              <MenuOpenIcon />
            </IconButton>
          </Box>
          <Box className={classes.logoImg} my={2}>
            <Link to="/" className={classes.link}>
              <Box>
                <Logo style={{ width: 157, height: 23 }} />
              </Box>
            </Link>
          </Box>
          {user && (
            <List>
              {menus.map((itm) => {
                return MenuItemButton(itm, [], false, drawerOpen);
              })}
            </List>
          )}
        </Box>
        <Box width="100%">
          {user && (
            <List>
              <StyledListItem isDrawerOpen={drawerOpen} className={classes.listItem}>
                <MenuButton
                  icon={<AccountCircle />}
                  name={`${user.first_name} ${user.last_name}`}
                  isSideBarOpen={drawerOpen}
                  enableToolTip={true}
                />
              </StyledListItem>
              <StyledListItem isDrawerOpen={drawerOpen} className={classes.listItem}>
                <MenuButton
                  icon={<SettingsOutlinedIcon />}
                  name={`Settings`}
                  isSideBarOpen={drawerOpen}
                  enableToolTip={true}
                  onClick={handleUserPreferencesDrawer}
                />
              </StyledListItem>
              <StyledListItem isDrawerOpen={drawerOpen} className={classes.listItem}>
                <MenuButton
                  icon={<Logout />}
                  name="Logout"
                  isSideBarOpen={drawerOpen}
                  enableToolTip={true}
                  onClick={() => {
                    logout();
                  }}
                />
              </StyledListItem>
            </List>
          )}
          {!user && drawerOpen && (
            <Box sx={{ m: 4 }}>
              <Typography textAlign="center">&copy; {new Date().getFullYear()} by Alternaleaf.</Typography>
              <Typography textAlign="center">All rights reserved.</Typography>
            </Box>
          )}
        </Box>
      </Box>
      <UserPreferencesDrawer open={userPreferences} setOpen={handleUserPreferencesDrawer} />
    </StyledDrawer>
  );
};
